import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, UntypedFormControl, UntypedFormGroup, ValidatorFn, Validators } from '@angular/forms';
import { State, getSteps } from '@app/app.reducer';
import { FieldKey } from '@core/data-access/models/dynamic-form-field/dynamic-form-field.model';
import { CheckedFormsValidity } from '@core/data-access/models/form/form-validity.model';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { TranslationsService } from '@pages/translations/translations.service';
import { TariffUuid } from '@products/models/tariff.model';
import { ValidationService } from '@shared/validation/validation.service';
import { SelectOption } from '@store/exhibition/exhibition.interface';
import { TextOrDropdownInputTypes } from '@store/helpers/helper.interface';
import { HelperService } from '@store/helpers/helper.service';
import {
  FormGroupValidationModel,
  FormInputsPayloadModel,
  FormStatusPayloadModel,
  StepsFormsActionName
} from '@store/step-forms/step.interface';
import { RemoveFormValidity, SetFormValidity, SetInputs } from '@store/step-forms/steps-forms.actions';
import { cloneDeep, difference, forEach, isEmpty, isNil, keys, merge, omit } from 'lodash';
import {
  BehaviorSubject,
  Observable,
  Subject,
  Subscriber,
  buffer,
  debounceTime,
  from,
  groupBy,
  map,
  mergeMap,
  startWith,
  toArray
} from 'rxjs';
import { InputBase } from './inputs/input-base.class';
import { TextInput } from './inputs/input-text.class';

@Injectable({
  providedIn: 'root'
})
export class FormsService {
  readonly #destroyRef = inject(DestroyRef);

  inputsValidity = {};
  selectedStepInputValidity = {};
  nextInvalidTicketHolderFormTariffUuid = '';

  validationFeedbacksUpdated$ = new BehaviorSubject(false);

  private subject = new Subject();
  private removeFormSubject = new Subject();
  private counter = 0;
  private formValidationCallbacks: FormGroupValidationModel[] = [];
  private validationInterval = null;

  #steps = this.store.selectSignal(getSteps);

  constructor(
    private store: Store<State>,
    private validationService: ValidationService,
    private helperService: HelperService,
    private translateService: TranslateService,
    private translationsService: TranslationsService
  ) {
    /* FOLLOWING CODE CATCH REMOVING FORMS VALIDATIONS AND CONCENTRATE THEM TO BATCH REMOVALS  */

    this.removeFormSubject
      .pipe(buffer(this.removeFormSubject.pipe(startWith([]), debounceTime(100))))
      .subscribe((formsToRemove: FormStatusPayloadModel[]) => {
        this.store.dispatch(new RemoveFormValidity(formsToRemove));
      });

    /* FOLLOWING CODE CATCH FORM CHANGES AND CONCENTRATE THEM TO BATCH CHANGES  */

    // this.subject accepts changed form ( mostly with only one input field changed )
    const subscription = this.subject
      .pipe(
        /**
         * group is needed so it cannot happen that someone edit two different forms within 100ms and mix their data together
         * streams are grouped to substreams based on uniq form ID (item.formInfo)
         */
        groupBy((item: FormInputsPayloadModel) => {
          return item.formInfo.join('.');
        }),
        /* flatmap handles, that substreams splited by form name
         * (once streamed forms are merged into a single one with latest values)
         * are again joined back to one final stream.
         */
        mergeMap((observableGroupByInputName) => {
          return observableGroupByInputName.pipe(
            /* collect forms in a stream into an array.
             * Buffer() accepts a stream (the same stream we observe in this case),
             * based on which it recognizes, when to stop buffering streamed data into array
             * and pass it further (into map() function in our case)
             */
            buffer(this.subject.pipe(startWith({}), debounceTime(100))),
            // use map() to turn the array of forms into one form with latest values
            map((arrayOfForms: FormInputsPayloadModel[]) => {
              if (!arrayOfForms.length) {
                return null;
              } else if (arrayOfForms.length === 1) {
                // if only one was buffered, send it separately
                return arrayOfForms[0];
              } else {
                // if more inputs changed at a time reduce the array into final form object
                const mergedResult = arrayOfForms.reduce(
                  (acc: FormInputsPayloadModel, current: FormInputsPayloadModel) => {
                    const merged: FormInputsPayloadModel = {
                      formInfo: acc.formInfo,
                      inputSet: { ...acc.inputSet, updatedInputs: null },
                      callback: acc.callback
                    };

                    /*
                     * Mass fill (automated filling form fields by browser based on prevously filled forms elsewehre)
                     * only add values (never remove ones), so we only replace old values with new existing oned
                     */

                    current.inputSet.list.forEach((input, index) => {
                      //SteZ_20201116:
                      //if we're coming from newInputValue function update only inputs from FormInputsPayloadModel.inputSet.updatedInputs property. if that property isn't defined do the usual magic inside those ifs.
                      //this is important in the following testing scenario:
                      //1. choose an event and a ticket,
                      //2. go to personalization, change the first name and immediately click on some dynamic checkbox.
                      //in this scenario we'd get two forms (the first one with the first name changed, the second one with the checkbox changed) and while merging the second form we'd lose the first name i.e. we'd return it to the previous value.
                      if (
                        current.inputSet.updatedInputs === undefined ||
                        current.inputSet.updatedInputs.includes(input.key)
                      ) {
                        if (
                          Object.hasOwn(current.inputSet.list[index], 'value') &&
                          current.inputSet.list[index].value
                        ) {
                          merged.inputSet.list[index].value = cloneDeep(current.inputSet.list[index].value);
                        }

                        if (current.inputSet.list[index].options.length) {
                          merged.inputSet.list[index].options = cloneDeep(current.inputSet.list[index].options);
                        }

                        if (current.callback) {
                          merged.callback = current.callback;
                        }
                      }
                    });
                    return merged;
                  }
                );
                // In case there are more merged inputs, prerender anyway.
                mergedResult.inputSet.rerender = true;

                return mergedResult;
              }
            })
          );
        })
      )
      /* at the end we have a stream of forms debounced by 100ms,
       * we can now save it without worry about flooding http requests with form save request
       */
      .subscribe((mergedInputs: FormInputsPayloadModel) => {
        if (mergedInputs) {
          // in case that formInfo has two parts and it has no callback it is saved to steps-forms reducer
          if (mergedInputs.formInfo.length === 2 && !mergedInputs.callback) {
            this.store.dispatch(new SetInputs(mergedInputs));
          }
          /** in case it has a callback, it is not steps form
           * and the form save need to be handled separately in the callback
           */
          if (mergedInputs.callback) {
            mergedInputs.callback(mergedInputs.inputSet.list, mergedInputs.inputSet.rerender);
          }
        }
        this.counter = 0;
      });
  }

  /** as values comes from single inputs, we want to save the form just once, after all inputs has changed
   *  It must be done this way as autofill could change multiple inputs at once. If merge save is not introduced here,
   *  all input values except the last one would be lost
   */

  mergeInputsBeforeSave(actionPayload: FormInputsPayloadModel) {
    if (++this.counter == 1) {
      this.subject.next(actionPayload);
    } else {
      setTimeout(() => {
        this.subject.next(actionPayload);
      }, 300);
    }
  }

  updateInputs(inputsToUpdate: InputBase<any>[], inputsWithNewValues: InputBase<any>[]) {
    if (!inputsWithNewValues.length) {
      return [];
    }

    if (inputsToUpdate) {
      inputsToUpdate.forEach((input: InputBase<any>, index) => {
        if (inputsWithNewValues[index]) {
          if (input.controlType === 'textbox' && inputsWithNewValues[index].value) {
            let newValue = inputsWithNewValues[index].value;

            if (!input.noTrim) {
              newValue = newValue.trim();
            }

            inputsWithNewValues[index].value = newValue;
          }

          input.value = inputsWithNewValues[index].value || '';
          input.disabled = inputsWithNewValues[index].disabled || false;
          input.options = inputsWithNewValues[index].options;
          input.hidden = inputsWithNewValues[index].hidden;
        }
      });
    } else {
      inputsToUpdate = cloneDeep(inputsWithNewValues);
    }

    return inputsToUpdate;
  }

  /**
   * This function will reset the Function (Position) value if its current value doesn't exists in settings.
   * NOTE: Be aware when mixing this function with synchronous code because "reset" is done inside subscription (asynchronously)
   */
  resetInvalidFunctionValue(
    input: InputBase<any>,
    availableTitles: SelectOption[],
    availableProfessions: SelectOption[],
    availableDepartments: SelectOption[],
    availableOccupationalGroups: SelectOption[],
    updateCb: (input: InputBase<any>, invalidTitles: boolean) => void,
    updateAlways?: boolean
  ) {
    const isTitleInput = input.key === TextOrDropdownInputTypes.Title;
    const isFunctionInput = input.key === TextOrDropdownInputTypes.Function;
    const isDepartmentInput = input.key === TextOrDropdownInputTypes.Department;
    const isOccupationalGroupInput = input.key === TextOrDropdownInputTypes.OccupationalGroup;

    if (isTitleInput) {
      const availableTitlesTranslationSource = from(availableTitles).pipe(
        mergeMap(
          (titles: SelectOption) =>
            new Observable((subscriber: Subscriber<string>) => {
              this.translationsService.getPositionTranslationForOption(input, titles, (translatedText: string) => {
                subscriber.next(translatedText);
                subscriber.complete();
              });
            })
        ),
        toArray()
      );

      availableTitlesTranslationSource.subscribe((availableTitles: string[]) => {
        const titlesInList = availableTitles.find((titlesText) => titlesText === input.value);
        const invalidTitles = !titlesInList && !!availableTitles.length;

        if (updateAlways || invalidTitles) {
          updateCb(input, invalidTitles);
        }
      });
    } else if (isFunctionInput) {
      const availableProfessionsTranslationSource = from(availableProfessions).pipe(
        mergeMap(
          (profession: SelectOption) =>
            new Observable((subscriber: Subscriber<string>) => {
              this.translationsService.getPositionTranslationForOption(input, profession, (translatedText: string) => {
                subscriber.next(translatedText);
                subscriber.complete();
              });
            })
        ),
        toArray()
      );

      availableProfessionsTranslationSource.subscribe((availableProfessions: string[]) => {
        const professionExistInList = availableProfessions.find((professionText) => professionText === input.value);
        const invalidProfession = !professionExistInList && !!availableProfessions.length;

        if (updateAlways || invalidProfession) {
          updateCb(input, invalidProfession);
        }
      });
    } else if (isDepartmentInput) {
      const availableDepartmentsTranslationSource = from(availableDepartments).pipe(
        mergeMap(
          (department: SelectOption) =>
            new Observable((subscriber: Subscriber<string>) => {
              this.translationsService.getPositionTranslationForOption(input, department, (translatedText: string) => {
                subscriber.next(translatedText);
                subscriber.complete();
              });
            })
        ),
        toArray()
      );

      availableDepartmentsTranslationSource.subscribe((availableDepartments: string[]) => {
        const departmentExistInList = availableDepartments.find((departmentText) => departmentText === input.value);
        const invalidDepartment = !departmentExistInList && !!availableDepartments.length;

        if (updateAlways || invalidDepartment) {
          updateCb(input, invalidDepartment);
        }
      });
    } else if (isOccupationalGroupInput) {
      const availableOccupationalGroupsTranslationSource = from(availableOccupationalGroups).pipe(
        mergeMap(
          (occupationalGroup: SelectOption) =>
            new Observable((subscriber: Subscriber<string>) => {
              this.translationsService.getPositionTranslationForOption(
                input,
                occupationalGroup,
                (translatedText: string) => {
                  subscriber.next(translatedText);
                  subscriber.complete();
                }
              );
            })
        ),
        toArray()
      );

      availableOccupationalGroupsTranslationSource.subscribe((availableOccupationalGroups: string[]) => {
        const occupationalGroupInList = availableOccupationalGroups.find(
          (occupationalGroupText) => occupationalGroupText === input.value
        );
        const invalidOccupationalGroup = !occupationalGroupInList && !!availableOccupationalGroups.length;

        if (updateAlways || invalidOccupationalGroup) {
          updateCb(input, invalidOccupationalGroup);
        }
      });
    }
  }

  inputsToFormControl(inputs: InputBase<any>[], formPath?: string) {
    const group: any = {};
    let selectedCountryCode = '';
    const globalFormValidators: ValidatorFn[] = [];

    if (inputs) {
      inputs
        .filter((input) => {
          return input.key === 'country' && input.value;
        })
        .map((input) => {
          selectedCountryCode = input.value;
        });

      inputs.forEach((input) => {
        if (input.controlType === 'checkbox') {
          const checkboxesGroup: any = {};

          input.options.forEach((option, index) => {
            const validators = [];

            if (option.required && !input.hidden) {
              validators.push(Validators.required);
            }

            checkboxesGroup[input.key + '_' + option.key] = new UntypedFormControl(
              {
                value: option.value || false,
                disabled: option.disabled || false
              },
              validators
            );
          });

          let chckboxValidator = null;
          if (input.required && !input.hidden) {
            chckboxValidator = ValidationService.checkboxGroupValidator;
          }
          const fg = new UntypedFormGroup(checkboxesGroup, chckboxValidator);

          group[input.key] = fg;
        } else {
          /* SETUP VALIDATORS */
          let validators = [];
          let asyncValidators = [];

          if (input.required) {
            validators.push(Validators.required);
          }

          if (input.maxLengthValidation) {
            validators.push(Validators.maxLength(input.maxLengthValidation));
          }

          if (input.minLengthValidation) {
            validators.push(Validators.minLength(input.minLengthValidation));
          }

          if (input.emailValidation) {
            validators.push(ValidationService.emailValidator(input.required));
          }

          if (input.zipcodeValidation) {
            // construct form path from input zipCodeCitiesTag so we know on which form we made changes to zip code input
            const { zipCodeCitiesTag } = input as TextInput;
            const formPath = zipCodeCitiesTag;
            asyncValidators.push(this.validationService.zipCodeValidator(selectedCountryCode, formPath));
          }

          if (input.phoneValidation) {
            validators.push(ValidationService.phoneValidator());
          }

          if (input.sameAsValidation) {
            globalFormValidators.push(
              ValidationService.equalValueValidator(input.key, input.sameAsValidation, input.caseInsensitive)
            );
          }

          if (input.controlType === 'textbox') {
            validators.push(ValidationService.emptySpacesValidator());
          }

          if (input.hidden) {
            validators = [];
            asyncValidators = [];
          }

          group[input.key] = new FormControl(
            {
              value:
                input.controlType === 'dateInput' ? (input.value ? new Date(input.value) : null) : input.value || '',
              disabled: input.disabled || input.hidden || false
            },
            validators,
            asyncValidators
          );
        }
      });

      return { group, globalFormValidators };
    }
  }

  removeFormsValidationFeedback(stepsFormsActionName?: StepsFormsActionName) {
    if (stepsFormsActionName && stepsFormsActionName.length === 2) {
      const [stepName, formName] = stepsFormsActionName;
      const form = this.#steps()[stepName].forms[formName];

      if (!isEmpty(form)) {
        const inputs = form.list;

        const validationObjectCopy = cloneDeep(this.inputsValidity[stepName]);

        inputs.forEach((input: InputBase<any>) => {
          delete validationObjectCopy[formName + '_' + input.key];
        });

        this.inputsValidity[stepName] = validationObjectCopy;
        this.validationFeedbacksUpdated$.next(true);
      }
    }
  }

  toFormGroup(inputs: InputBase<any>[], stepsFormsActionName?: StepsFormsActionName) {
    if (inputs) {
      const isValidStepFormActionName = !isEmpty(stepsFormsActionName) && stepsFormsActionName.length === 2;

      const formControls = this.inputsToFormControl(inputs, isValidStepFormActionName ? stepsFormsActionName[1] : null);
      const stepsForm = new UntypedFormGroup(formControls.group);

      stepsForm.setValidators(formControls.globalFormValidators);

      // as there is a bug in AG4 forms, FormGroup with only required checkboxes
      // is marked as valid on init, so we need to wait and recheck validity

      // In case form uses async validators we are not able to get the validity when created
      // The solution is to wait until the form is not pending anymore

      //SteZ: but do it in the order of arrival, not randomly
      //(as then the first form validation could be executed last which could mean the form would be set as valid even though it really isn't):
      this.formValidationCallbacks.push({ formActionName: stepsFormsActionName, form: stepsForm });
      this.processFormValidationQueue();

      if (isValidStepFormActionName) {
        // recheck validity on any form status(validity) change
        stepsForm.statusChanges.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe(() => {
          this.setFormValidity(!stepsForm.invalid, stepsForm, stepsFormsActionName);
        });
      }

      return stepsForm;
    }
  }

  /**
   * This function will process form validation callbacks in FIFO fashion but it will call setFormValidity function only for the last form validation callback of one kind i.e. one formActionName.
   * If a form is still in pending status the function will wait for it to exit that status.
   */
  processFormValidationQueue() {
    //only proceed if validationInterval is not set or not running:
    if (this.validationInterval === null || this.validationInterval.state !== 'scheduled') {
      //set validationInterval to run every 100ms:
      this.validationInterval = setInterval(() => {
        //proceed only if we have formValidationCallbacks to process:
        if (!!this.formValidationCallbacks && this.formValidationCallbacks.length) {
          while (!!this.formValidationCallbacks && this.formValidationCallbacks.length) {
            //get the first form validation callback in the queue:
            const firstCallback: FormGroupValidationModel = this.formValidationCallbacks[0];

            //check if we have more form validation callbacks of the same formActionName:
            const allCallbacksSameAsFirst: FormGroupValidationModel[] = this.formValidationCallbacks.filter(
              (callback) =>
                !!callback.formActionName &&
                !!firstCallback.formActionName &&
                callback.formActionName.toString() === firstCallback.formActionName.toString()
            );

            if (!!allCallbacksSameAsFirst && allCallbacksSameAsFirst.length > 1) {
              //if we have more form validation callbacks of the same formActionName skip the current one (process only the last one):
              this.formValidationCallbacks.splice(0, 1);
            } else {
              //only process the last form validation callback of the same formActionName:
              if (!firstCallback.form.pending) {
                //the form is no longer in pending status so we can call setFormValidity:
                this.setFormValidity(!firstCallback.form.invalid, firstCallback.form, firstCallback.formActionName);

                //remove the already processed form validation callback:
                this.formValidationCallbacks.splice(0, 1);
              } else {
                //the form is in pending status so we have to wait a little bit longer:
                break;
              }
            }
          }
        } else {
          //we have no more form validation callbacks to process so we have to cancel the periodic execution of validationInterval:
          clearInterval(this.validationInterval);
        }
      }, 100);
    }
  }

  // write down status(valid === true, invalid === false) of current form into the steps forms store
  setFormValidity(valid: boolean, form: UntypedFormGroup, stepsFormsActionName?: StepsFormsActionName) {
    if (isEmpty(stepsFormsActionName)) {
      return;
    }

    this.updateValidationFeedbacks(form, stepsFormsActionName);

    if (stepsFormsActionName.length === 2) {
      const actionPayload: FormStatusPayloadModel = {
        formInfo: stepsFormsActionName,
        valid: valid
      };

      this.store.dispatch(new SetFormValidity(actionPayload));
    }
  }

  updateValidationFeedbacks(form: UntypedFormGroup, stepsFormsActionName?: StepsFormsActionName) {
    if (isEmpty(stepsFormsActionName) || stepsFormsActionName.length !== 2 || isNil(form)) {
      return;
    }

    const [stepName, formName] = stepsFormsActionName;

    const stepForm = this.#steps()[stepName]?.forms[formName];

    if (isEmpty(stepForm)) {
      return;
    }

    this.inputsValidity[stepName] = this.generateValidationFeedback(
      stepForm.list,
      form,
      formName,
      this.inputsValidity[stepName] || {}
    );

    this.validationFeedbacksUpdated$.next(true);
  }

  setCheckedFormsValidityValidationFeedback(selectedStep: string, checkedFormsValidity: CheckedFormsValidity) {
    const selectedStepInputValidity = {};

    forEach(checkedFormsValidity, (formValidity) => {
      forEach(formValidity.invalidFormFields, (invalidFormField, fieldKey) => {
        const isPrivacyPolicyCheckbox = fieldKey === FieldKey.Disclaimer || fieldKey === FieldKey.DisclaimerOptional;

        selectedStepInputValidity[fieldKey] = {
          label: invalidFormField.label,
          translate: !isPrivacyPolicyCheckbox
        };
      });
    });

    const selectedStepInputsValidity = this.inputsValidity[selectedStep];
    const currentSelectedStepInputValidityKeys = keys(selectedStepInputValidity);
    const previousSelectedStepInputValidityKeys = keys(this.selectedStepInputValidity);
    const previousInvalidCurrentValidInputKeys = difference(
      previousSelectedStepInputValidityKeys,
      currentSelectedStepInputValidityKeys
    );
    const previousInputValidityKeys = keys(selectedStepInputsValidity);
    const currentInputValidityKeys = keys(omit(selectedStepInputsValidity, previousInvalidCurrentValidInputKeys));
    const removedValidKeys = isEmpty(selectedStepInputValidity)
      ? previousInputValidityKeys
      : difference(previousInputValidityKeys, currentInputValidityKeys);

    this.selectedStepInputValidity = selectedStepInputValidity;

    this.inputsValidity[selectedStep] = omit(
      merge({}, this.selectedStepInputValidity, selectedStepInputsValidity),
      removedValidKeys
    );

    this.validationFeedbacksUpdated$.next(true);
  }

  setNextInvalidTicketHolderFormTariffUuid(tariffUuid: TariffUuid) {
    this.nextInvalidTicketHolderFormTariffUuid = tariffUuid;
  }

  // write down status(valid === true, invalid === false) of current form into the steps forms store
  removeFormValidity(stepsFormsActionName?: StepsFormsActionName) {
    const actionPayload: FormStatusPayloadModel = {
      formInfo: stepsFormsActionName,
      valid: false
    };
    if (stepsFormsActionName && stepsFormsActionName.length === 2) {
      this.removeFormSubject.next(actionPayload);
    }
  }

  generateValidationFeedback(
    inputs: InputBase<any>[],
    form: UntypedFormGroup,
    formName: string,
    validationObject: any
  ) {
    const validationObjectCopy = cloneDeep(validationObject);

    if (inputs) {
      inputs.forEach((input: InputBase<any>) => {
        if (form.controls[input.key] && form.controls[input.key].invalid && !input.hidden) {
          const label = input.label || input.options[0].label;
          validationObjectCopy[formName + '_' + input.key] = {
            translate: input.translate,
            label: label && label.length > 0 ? label.replace(/<(?:.|\n)*?>/gm, '') : label // this one avoid html markup to be shown in the message
          };
        } else {
          delete validationObjectCopy[formName + '_' + input.key];
        }
      });
    }

    return validationObjectCopy;
  }

  addStepValidationFeedback(stepsFormsActionName: StepsFormsActionName, key: string, messageTranslationKey: string) {
    if (key == '') {
      this.inputsValidity = {};
      this.validationFeedbacksUpdated$.next(true);
    } else {
      const [stepName, formName] = stepsFormsActionName;

      this.inputsValidity[stepName] = this.inputsValidity[stepName] || {};

      this.inputsValidity[stepName][formName + '_' + key] = {
        translate: true,
        label: messageTranslationKey
      };
      this.validationFeedbacksUpdated$.next(true);
    }
  }

  removeStepValidationFeedback(stepsFormsActionName: StepsFormsActionName, key: string) {
    const [stepName, formName] = stepsFormsActionName;

    if (
      key &&
      Object.hasOwn(this.inputsValidity, stepName) &&
      Object.hasOwn(this.inputsValidity[stepName], formName + '_' + key)
    ) {
      delete this.inputsValidity[stepName][formName + '_' + key];
      this.validationFeedbacksUpdated$.next(true);
    }
  }

  removeAllStepValidationFeedbacks(stepsFormsActionName: StepsFormsActionName) {
    const [stepName, formName] = stepsFormsActionName;

    if (Object.hasOwn(this.inputsValidity, stepName)) {
      // delete validation inputs when form is removed from the flow (now used after ticketHolder form is removed, or billing address unchecked)
      Object.keys(this.inputsValidity[stepName]).forEach((inputValidationKey: string) => {
        if (inputValidationKey.indexOf(formName) >= 0) {
          delete this.inputsValidity[stepName][inputValidationKey];
          this.validationFeedbacksUpdated$.next(true);
        }
      });
    }
  }

  setStepValid(stepsFormsActionName: StepsFormsActionName) {
    // set the whole form valid in reducer
    this.setFormValidity(true, null, stepsFormsActionName);
    this.removeAllStepValidationFeedbacks(stepsFormsActionName);
  }

  removeStepValidation(stepsFormsActionName: StepsFormsActionName) {
    // set the whole form valid in reducer
    this.removeFormValidity(stepsFormsActionName);
    this.removeAllStepValidationFeedbacks(stepsFormsActionName);
  }

  checkStepValidationFeedbacks(stepsFormsActionName: StepsFormsActionName) {
    if (
      Object.hasOwn(this.inputsValidity, stepsFormsActionName[0]) &&
      Object.keys(this.inputsValidity[stepsFormsActionName[0]]).length
    ) {
      return false;
    }

    return true;
  }

  translateCountries(preferredList) {
    if (!preferredList) {
      return from([]);
    }

    return this.helperService.loadCountries().pipe(
      map((p: any) => {
        const translationMap = p.map((country) => `country.${country.shortCode}`);

        return this.translateService.stream(translationMap).pipe(
          map((termsTranslation) =>
            Object.keys(termsTranslation)
              .map((key) => ({
                key: key.replace('country.', ''),
                value: termsTranslation[key],
                translate: false
              }))
              // first full preferred to top
              .sort((a, b) => {
                const preferredIndexA: number = preferredList.indexOf(a.key);
                const preferredIndexB: number = preferredList.indexOf(b.key);

                if (preferredIndexA > -1 && preferredIndexB > -1) {
                  //if both countries are in preferred countries list:
                  if (preferredIndexA < preferredIndexB) {
                    return -1;
                  } else {
                    return 1;
                  }
                }

                if (preferredIndexA > -1) {
                  return -1;
                }

                if (preferredIndexB > -1) {
                  return 1;
                } else {
                  return a.value.localeCompare(b.value);
                }
              })
          )
        );
      })
    );
  }

  resetInputsValidity() {
    this.inputsValidity = {};
    this.validationFeedbacksUpdated$.next(true);
  }
}
